/**
 * University sections written as Markdown files: `docs/<page-slug>/NN-<section>.md`, one file
 * per section, each with a frontmatter (`title`, `section`, …). A folder's README.md is its
 * index: its numbered `## N. Group` headings group the sections listed under them.
 * "## For the assistant" is kept out of the page and passed to Atlas only.
 */
import type { UniversitySection } from "./content";

const FILES = import.meta.glob<string>("./docs/*/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
});

const ASSISTANT_HEADING = "## For the assistant";

type DocFile = { page: string; file: string; raw: string };

type ParsedDoc = { id: string; title: string; body: string; assistant: string };

function splitFrontmatter(raw: string): { meta: Record<string, string>; body: string } {
  const lines = raw.split("\n");
  if (lines[0]?.trim() !== "---") {
    return { meta: {}, body: raw };
  }
  const end = lines.indexOf("---", 1);
  const meta: Record<string, string> = {};
  for (const line of lines.slice(1, end)) {
    const colon = line.indexOf(":");
    if (colon > 0) {
      // YAML-style quotes around a value ("Title: subtitle") are not part of it.
      meta[line.slice(0, colon).trim()] = line
        .slice(colon + 1)
        .trim()
        .replace(/^"(.*)"$/, "$1");
    }
  }
  return { meta, body: lines.slice(end + 1).join("\n") };
}

/** Splits the body into what readers see and the "For the assistant" notes. */
function splitAssistant(body: string): { body: string; assistant: string } {
  const lines = body.split("\n");
  const start = lines.findIndex((l) => l.trim() === ASSISTANT_HEADING);
  if (start < 0) {
    return { body, assistant: "" };
  }
  const next = lines.findIndex((l, i) => i > start && l.startsWith("## "));
  const end = next < 0 ? lines.length : next;
  return {
    body: [...lines.slice(0, start), ...lines.slice(end)].join("\n"),
    assistant: lines
      .slice(start + 1, end)
      .join("\n")
      .trim(),
  };
}

/** Folder → University page slug; the Deals folder backs the "Deals handbook" page. */
const pageSlug = (folder: string): string => (folder === "deals" ? "deals-handbook" : folder);

const fileSlug = (file: string) => file.replace(/^\d+-/, "").replace(/\.md$/, "");

function parseDoc({ file, raw }: DocFile): ParsedDoc {
  const { meta, body } = splitFrontmatter(raw);
  // The page renders the title itself, so the file's own H1 is dropped.
  const withoutTitle = body
    .split("\n")
    .filter((l) => !l.startsWith("# "))
    .join("\n");
  const { body: visible, assistant } = splitAssistant(withoutTitle);
  return {
    id: meta.section || fileSlug(file),
    title: meta.title || fileSlug(file),
    body: rulesToTables(visible).trim(),
    assistant,
  };
}

/** Maps each section file to its README group, when the README has two or more numbered groups. */
function readmeGroups(readme: string | undefined): Map<string, string> {
  const groups = new Map<string, string>();
  let current = "";
  for (const line of (readme ?? "").split("\n")) {
    const heading = /^## (\d+)\.\s+(.+)$/.exec(line);
    if (heading) {
      current = heading[2].trim();
      continue;
    }
    const link = /\]\((\d+-[^)]+\.md)\)/.exec(line);
    if (current && link) {
      groups.set(link[1], current);
    }
  }
  return new Set(groups.values()).size > 1 ? groups : new Map();
}

const RULE_START = "- **Rule:**";
const EXAMPLE = /[*_]Example today:[*_]/;

/** A table cell: pipes escaped, first letter capitalised. */
const cell = (text: string) => {
  const t = text.trim().replaceAll("|", String.raw`\|`);
  return t.charAt(0).toUpperCase() + t.slice(1);
};

/** One "- **Rule:** … *Example today:* …" bullet (with its indented lines) as a table row. */
function ruleRow(item: string[]): string {
  const text = item
    .map((l) => l.trim())
    .join(" ")
    .slice(RULE_START.length);
  const [rule, example = ""] = text.split(EXAMPLE);
  return `| ${cell(rule)} | ${cell(example)} |`;
}

/** Runs of Rule bullets read better side by side: Rules | Example today. */
function rulesToTables(body: string): string {
  const lines = body.split("\n");
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    if (!lines[i].startsWith(RULE_START)) {
      out.push(lines[i]);
      i++;
      continue;
    }
    const rows: string[] = [];
    // Quoted notes (e.g. "> **To confirm:**") under a rule are not part of its example.
    const notes: string[] = [];
    while (i < lines.length && lines[i].startsWith(RULE_START)) {
      const item = [lines[i++]];
      while (i < lines.length && /^\s+\S/.test(lines[i])) {
        const line = lines[i++];
        (line.trim().startsWith(">") ? notes : item).push(line.trim());
      }
      rows.push(ruleRow(item));
      // Blank lines between two Rule bullets don't end the table.
      let next = i;
      while (next < lines.length && lines[next].trim() === "") {
        next++;
      }
      if (lines[next]?.startsWith(RULE_START)) {
        i = next;
      }
    }
    out.push("", "| Rules | Example today |", "| --- | --- |", ...rows, "", ...notes);
  }
  return out.join("\n");
}

/**
 * List items name other sections as `page/section` (See also lines, sometimes several per
 * line); turn every known one into a link titled after the section.
 */
function linkSeeAlso(body: string, titles: Map<string, string>): string {
  return body
    .split("\n")
    .map((line) =>
      line.startsWith("- ")
        ? line.replaceAll(
            /`?(?<![\w/(-])([a-z-]+\/[a-z0-9-]+)(?![\w/-])`?/g,
            (match, ref: string) => {
              const title = titles.get(ref);
              const [folder, id] = ref.split("/");
              return title ? `[${title}](${pageSlug(folder)}/${id})` : match;
            },
          )
        : line,
    )
    .join("\n");
}

const docFiles: DocFile[] = Object.entries(FILES)
  .map(([path, raw]) => {
    const [, page, file] = path.split("/").slice(-3);
    return { page, file, raw };
  })
  // Code-unit order, not the browser's locale: the Atlas catalog must be byte-identical everywhere
  // so the API's prompt cache hits.
  .sort((a, b) => Number(a.file > b.file) - Number(a.file < b.file));

const sectionFiles = docFiles.filter((d) => d.file !== "README.md");
const parsed = sectionFiles.map((d) => ({ ...d, doc: parseDoc(d) }));
const titles = new Map(parsed.map(({ page, doc }) => [`${page}/${doc.id}`, doc.title]));

/** Sections per University page slug, in file order. */
export const DOC_SECTIONS: Record<string, UniversitySection[]> = {};

for (const { page, file, doc } of parsed) {
  const readme = docFiles.find((d) => d.page === page && d.file === "README.md")?.raw;
  const group = readmeGroups(readme).get(file);
  const sections = (DOC_SECTIONS[pageSlug(page)] ??= []);
  // Ids anchor links and citations, so two files claiming the same section keep distinct ids.
  const taken = sections.some((s) => s.id === doc.id);
  sections.push({
    id: taken ? `${doc.id}-${fileSlug(file)}` : doc.id,
    title: doc.title,
    ...(group ? { group } : {}),
    blocks: [
      {
        type: "markdown",
        text: linkSeeAlso(doc.body, titles),
        ...(doc.assistant ? { assistant: doc.assistant } : {}),
      },
    ],
  });
}
